import { Grid, Chip, Typography, Box, TextField, MenuItem } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { apiConstants, getGeneric } from "../../common/constants";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import Loader from "../../common/Loader";
import { formatDate } from "../../helpers/UtilityHelper";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PublicIcon from "@mui/icons-material/Public";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const columns = [
    {
        field: 'event',
        headerName: 'Event Name',
        width: 200,
        flex: 1,
    },
    {
        field: 'date',
        headerName: 'Date',
        width: 150,
        flex: 0.8,
        renderCell: (params) => formatDate(params?.row?.date,'dd MMM, yyyy'),
    },
    {
        field: 'day',
        headerName: 'Day',
        width: 130,
        flex: 0.6,
    },
    {
        field: 'hTYpe',
        headerName: 'Type',
        width: 130,
        flex: 0.6,
        renderCell: (params) => (
            <Chip
                label={params?.row?.hTYpe || '-'}
                color={params?.row?.hTYpe === 'NATIONAL' ? 'success' : 'secondary'}
                size="small"
                variant="filled"
            />
        ),
    },
    {
        field: 'pincode',
        headerName: 'Pincode',
        width: 130,
        flex: 0.6,
        renderCell: (params) => params?.row?.fromToPincodes?.to || '-',
    },
];

function StatCard({ icon, label, value, color }) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
            border: '1px solid #e0e0e0', borderRadius: 2, flex: 1, backgroundColor: '#fafafa'
        }}>
            <Box sx={{
                width: 40, height: 40, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', backgroundColor: `${color}15`,
            }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1}>{value}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
        </Box>
    );
}

export default function HolidayListView({ refreshKey }) {
    const [loading, setLoading] = useState(false);
    const [holidays, setHolidays] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');

    useEffect(() => {
        fetchHolidays();
    }, [refreshKey]);

    const fetchHolidays = async () => {
        setLoading(true);
        try {
            const response = await getGeneric(apiConstants.GET_EDDHOLIDAYS);
            const data = response?.data?.eddHolidays || [];
            const flattened = [];
            data.forEach((holiday, hIndex) => {
                if (holiday.dates && holiday.dates.length > 0) {
                    holiday.dates.forEach((dateItem, dIndex) => {
                        flattened.push({
                            id: `${hIndex}-${dIndex}`,
                            event: dateItem.event,
                            date: dateItem.date,
                            day: dateItem.day,
                            hTYpe: holiday.hTYpe,
                            fromToPincodes: holiday.fromToPincodes,
                        });
                    });
                } else {
                    flattened.push({
                        id: `${hIndex}`,
                        hTYpe: holiday.hTYpe,
                        fromToPincodes: holiday.fromToPincodes,
                        event: '-',
                        date: '-',
                        day: '-',
                    });
                }
            });
            setHolidays(flattened);
        } catch (error) {
            console.error("Error fetching holidays:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredHolidays = useMemo(() => {
        let filtered = holidays;
        if (typeFilter !== 'ALL') {
            filtered = filtered.filter((h) => h.hTYpe === typeFilter);
        }
        if (search.trim()) {
            const s = search.toLowerCase();
            filtered = filtered.filter((h) =>
                h.event?.toLowerCase().includes(s) ||
                h.fromToPincodes?.to?.includes(s)
            );
        }
        filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
        return filtered;
    }, [holidays, typeFilter, search]);

    const nationalCount = holidays.filter((h) => h.hTYpe === 'NATIONAL').length;
    const regionalCount = holidays.filter((h) => h.hTYpe === 'REGIONAL').length;

    if (loading) return <Loader />;

    if (holidays.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <EventBusyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">No holidays found</Typography>
                <Typography variant="body2" color="text.disabled">
                    Use the buttons above to upload or add holidays
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            {/* Stats */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <StatCard
                        icon={<CalendarMonthIcon sx={{ color: '#1976d2' }} />}
                        label="Total Holidays"
                        value={holidays.length}
                        color="#1976d2"
                    />
                    <StatCard
                        icon={<PublicIcon sx={{ color: '#2e7d32' }} />}
                        label="National"
                        value={nationalCount}
                        color="#2e7d32"
                    />
                    <StatCard
                        icon={<LocationOnIcon sx={{ color: '#9c27b0' }} />}
                        label="Regional"
                        value={regionalCount}
                        color="#9c27b0"
                    />
                </Box>
            </Grid>

            {/* Filters */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search by event or pincode..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: 300 }}
                    />
                    <TextField
                        size="small"
                        select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        sx={{ width: 180 }}
                        label="Type"
                    >
                        <MenuItem value="ALL">All Types</MenuItem>
                        <MenuItem value="NATIONAL">National</MenuItem>
                        <MenuItem value="REGIONAL">Regional</MenuItem>
                    </TextField>
                </Box>
            </Grid>

            {/* Table */}
            <Grid item xs={12}>
                <ProzoDataGrid
                    columns={columns}
                    rows={filteredHolidays}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    page={page}
                    setPage={setPage}
                    pagination
                    hideFooterPagination={false}
                    rowCount={filteredHolidays.length}
                />
            </Grid>
        </Grid>
    );
}
